import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { STATUS_LABEL, STATUS_BADGE, PAYMENT_BADGE, STATUS_STEPS } from "@/lib/statusLabels";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [services, setServices] = useState([]);
  const [serviceFilter, setServiceFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    if (statusFilter && statusFilter !== "all") params.status = statusFilter;
    if (paymentFilter && paymentFilter !== "all") params.payment_status = paymentFilter;
    if (serviceFilter && serviceFilter !== "all") params.service = serviceFilter;
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data.orders);
    setTotal(data.total);
    setLoading(false);
  }, [page, limit, q, statusFilter, paymentFilter, serviceFilter]);

  useEffect(() => {
    api.get("/services?active_only=false").then((r) => setServices(r.data.services));
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <DashboardLayout admin>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="section-label">Admin</div>
          <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">All Orders</h1>
        </div>
        <div className="text-sm text-slate-500" data-testid="admin-orders-count">{total} order{total === 1 ? "" : "s"}</div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search: order #, name, email, mobile" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} className="pl-9" data-testid="admin-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
          <SelectTrigger data-testid="admin-status-filter"><SelectValue placeholder="Status: All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_STEPS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={(v) => { setPage(1); setPaymentFilter(v); }}>
          <SelectTrigger data-testid="admin-payment-filter"><SelectValue placeholder="Payment: All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={serviceFilter} onValueChange={(v) => { setPage(1); setServiceFilter(v); }}>
          <SelectTrigger data-testid="admin-service-filter"><SelectValue placeholder="Service: All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {services.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? <div className="p-6 text-slate-500 text-sm">Loading…</div> :
        orders.length === 0 ? <div className="p-10 text-center text-slate-500">No orders found.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Order #</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`admin-order-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold">{o.order_number || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0A192F]">{o.full_name}</div>
                    <div className="text-xs text-slate-500">{o.email} · {o.mobile}</div>
                  </td>
                  <td className="px-4 py-3">{o.service_name}</td>
                  <td className="px-4 py-3"><span className={`status-badge ${PAYMENT_BADGE[o.payment_status]}`}>{o.payment_status}</span></td>
                  <td className="px-4 py-3"><span className={`status-badge ${STATUS_BADGE[o.current_status]}`}>{STATUS_LABEL[o.current_status]}</span></td>
                  <td className="px-4 py-3 text-slate-600">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right font-medium">₹{o.amount}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/orders/${o.id}`}>
                      <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`admin-view-${o.id}`}>Open</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <div className="p-4 flex items-center justify-between border-t border-slate-200 text-sm">
          <div className="text-slate-500">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="admin-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
